import { useCallback, useState, type PropsWithChildren } from 'react';

import type { User } from '@models/user.model';
import backendHttpClient from '@services/_httpClient/backend.http-client';
import { sessionService } from '@services/sessions/service';

import { router } from '../../main';
import type { IAuthorization } from './IUseAuth';
import { AuthContext } from './context';

type AuthData = {
  authorization: IAuthorization;
  user: User | null;
};

export const StorageKeyAuthorization = `GPMED::auth_authorization_1765202371960`;
const StorageKeyCredentials = `GPMED::auth_credentials_1765202371960`;

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [data, setData] = useState<AuthData>(() => {
    const storageToken = localStorage.getItem(StorageKeyAuthorization);
    const storedAuthCredentials = localStorage.getItem(StorageKeyCredentials);
    let token: string | null = null;
    let refreshToken: string | null = null;
    let storedAuthCredentialsParsed: User | null = null;
    if (storageToken) {
      const storedTokenParsed: IAuthorization = JSON.parse(storageToken);
      token = storedTokenParsed.token;
      refreshToken = storedTokenParsed.refreshToken;
    }
    if (storedAuthCredentials) {
      storedAuthCredentialsParsed = JSON.parse(storedAuthCredentials);
    }
    return {
      authorization: { token, refreshToken },
      user: storedAuthCredentialsParsed,
    };
  });

  const handleLogout = useCallback((path?: string) => {
    localStorage.removeItem(StorageKeyAuthorization);
    localStorage.removeItem(StorageKeyCredentials);
    backendHttpClient.removeBearerToken();
    setData({
      user: null,
      authorization: { token: null, refreshToken: null },
    });
    if (path) {
      router.navigate({
        to: path,
        replace: true,
      });
      return;
    }
    router.navigate({
      to: '/sign-in',
      replace: true,
    });
  }, []);

  const handleSetUser = useCallback(
    ({
      crm,
      document,
      documents,
      email,
      id,
      isActive,
      medicalSpecialties,
      name,
      phone,
    }: User) => {
      const userData: User = {
        email,
        id,
        name,
        isActive,
        phone,
        crm,
        documents,
        document,
        medicalSpecialties,
      };
      setData(oldState => ({
        ...oldState,
        user: userData,
      }));
      localStorage.setItem(StorageKeyCredentials, JSON.stringify(userData));
    },
    [],
  );

  const handleSetAuthorization = useCallback(
    ({ refreshToken, token }: IAuthorization) => {
      localStorage.setItem(
        StorageKeyAuthorization,
        JSON.stringify({ token, refreshToken }),
      );
      setData(oldState => ({
        ...oldState,
        authorization: {
          token,
          refreshToken,
        },
      }));
    },
    [],
  );

  const handleSessionInfo = useCallback(async () => {
    const userInfo = await sessionService.getInfo();
    handleSetUser(userInfo);
  }, [handleSetUser]);

  return (
    <AuthContext.Provider
      value={{
        authorization: data.authorization,
        onLogout: handleLogout,
        onSetAuthorization: handleSetAuthorization,
        onSetUser: handleSetUser,
        user: data.user,
        onSessionInfo: handleSessionInfo,
      }}>
      {children}
    </AuthContext.Provider>
  );
};
